<template>
  <v-container grid-list-md>
    <v-card class="rounded-t" tile fluid>
      <v-card-title class="pa-4 primary white--text">
        <v-container class="pb-1">
          <v-text-field
            dark
            v-model="title"
            color="white"
            label="Article Title"
            :rules="[rules.required]"
            :autofocus="isNewArticle"
          ></v-text-field>
        </v-container>
        <v-btn
          class="default-case text-subtitle-1 ml-3 mr-2 mb-1"
          @click="showHierarchy = !showHierarchy"
        >
          {{
            '/ ' +
            uriHead.split('/').join(' / ') +
            (uriHead.length > 0 ? ' /' : '')
          }}
        </v-btn>
        <span id="uri-input">
          <v-text-field
            dark
            v-model="uriEnd"
            :rules="[
              rules.required,
              rules.validCharacters,
              rules.whiteSpace,
              rules.special,
            ]"
            label="Article Path"
          >
          </v-text-field>
        </span>
      </v-card-title>
    </v-card>
    <v-row v-show="showHierarchy" class="pa-0">
      <v-col>
        <v-card tile class="hierarchy-card">
          <v-card-text class="ml-4">
            Select the article you want this one to appear under. Select root
            for top level articles. Select a parent article for its children to appear.
            {{
              isNewArticle
                ? ''
                : ' If this article has any children, they will also move.'
            }}
            <ArticleHierarchy
              @uriClick="setUriHead"
              :selectedUri="uriHead"
              :hiddenUri="isNewArticle ? '@' : currentArticle.uri"
              :editMode="!isNewArticle"
              showRoot
              clickable
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col xs="12" class="pt-0 pr-0">
        <v-card tile fluid class="rounded-bl markdown-content pa-5 fill-height">
          <editor
            ref="mainEditor"
            mode="wysiwyg"
            :options="editorOptions.main"
            height="auto"
            :initialValue="currentArticle.content"
            initialEditType="wysiwyg"
            :key="mainEditorKey"
            @change="onMainContentChanged"
          ></editor>
          <p class="mb-0 mt-4">
            To insert a
            <a href="https://transmission.um.city/">transmission.um.city</a>
            shared folder into the article, write the root folder name enclosed
            by two $ characters on each end. E.g., for
            <a href="https://transmission.um.city/l/DATA"
              >transmission.um.city/l/DATA</a
            >, write $$DATA$$ where you want the file browser to appear in the
            article.
          </p>
        </v-card>
      </v-col>

      <v-col xs="12" md="3" class="pt-0 pl-0">
        <sidebar :key="sidebarEditorKey" editable class="fill-height">
          <editor
            ref="sidebarEditor"
            mode="wysiwyg"
            :options="editorOptions.sidebar"
            height="auto"
            :initialValue="currentArticle.contentSidebar"
            initialEditType="wysiwyg"
            :key="sidebarEditorKey"
            @change="onSidebarContentChanged"
          ></editor>
        </sidebar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import 'codemirror/lib/codemirror.css'
import '@toast-ui/editor/dist/toastui-editor.css'
import { Editor } from '@toast-ui/vue-editor'
import { mapState, mapMutations } from 'vuex'

import Sidebar from './Sidebar'
import ArticleHierarchy from './ArticleHierarchy.vue'
export default {
  components: {
    Editor,
    Sidebar,
    ArticleHierarchy
  },
  props: {
    isNewArticle: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      uriHead: '',
      uriEnd: '',
      showHierarchy: false,
      rules: {
        required: value => !!value || 'Required.',
        // eslint-disable-next-line no-useless-escape
        validCharacters: value => !/[^\w\.~ \-]/.test(value) || 'Only alphanumeric, dashes, underscores, periods, and tildes.',
        whiteSpace: value => !/ /.test(value) || 'Use dashes instead of spaces.',
        special: value => !(value === 'edit' || value === 'new' || value === 'history') || 'Cannot be named just edit, new, or history'
      },
      editorOptions: {
        main: {
          minHeight: '500px',
          usageStatistics: false
        },
        sidebar: {
          usageStatistics: false
        }
      },
      mainEditorKey: 0,
      sidebarEditorKey: 0
    }
  },
  mounted () {
    if (this.isNewArticle) {
      this.currentArticle.content = ''
      this.currentArticle.contentSidebar = ''
      this.currentArticle.roles.read = []
      this.currentArticle.roles.write = []
      this.currentArticle.tags = []
      this.currentArticle.title = ''
      this.currentArticle.uri = this.$route.params.pathMatch
      this.mainEditorKey = this.mainEditorKey + 1
      this.sidebarEditorKey = this.sidebarEditorKey + 1
    } else {
      this.copyArticleForEdit()
    }
    this.uriHead = this.currentArticle.uri.split('/').slice(0, -1).join('/')
    this.uriEnd = this.currentArticle.uri.split('/').slice(-1).join('/')
  },
  methods: {
    ...mapMutations('article', [
      'editArticleField',
      'copyArticleForEdit',
      'setArticleValid'
    ]),
    setUriHead (uri) {
      this.uriHead = uri
    },
    updateField (key, value) {
      const payload = { key, value }
      this.editArticleField(payload)
    },
    onMainContentChanged () {
      this.updateField('content', this.$refs.mainEditor.invoke('getMarkdown'))
    },
    onSidebarContentChanged () {
      this.updateField('contentSidebar', this.$refs.sidebarEditor.invoke('getMarkdown'))
    },
    checkInputs (title, uriend) {
      this.setArticleValid(
        typeof (this.rules.required(uriend)) === 'boolean' &&
        typeof (this.rules.validCharacters(uriend)) === 'boolean' &&
        typeof (this.rules.whiteSpace(uriend)) === 'boolean' &&
        typeof (this.rules.special(uriend)) === 'boolean' &&
        title.length > 0
      )
    }
  },
  watch: {
    uriHead (val) {
      this.updateField('uri', (val.length > 0 ? val + '/' : '') + this.uriEnd)
    },
    uriEnd (val) {
      this.checkInputs(this.title, val)
      this.updateField('uri', (this.uriHead.length > 0 ? this.uriHead + '/' : '') + val)
    }
  },
  computed: {
    ...mapState('article', {
      currentArticle: state => state.currentArticle,
      articleHierarchy: state => state.articleHierarchy
    }),
    title: {
      get () {
        return this.currentArticle.title
      },
      set (val) {
        this.checkInputs(val, this.uriEnd)
        this.updateField('title', val)
        this.currentArticle.title = val
      }
    }
  }
}
</script>

<style>
#uri-input .v-text-field {
  width: 300px;
}
.default-case {
  text-transform: none !important;
}
.hierarchy-card {
  background-color: #f2f2f2 !important;
  color: black !important;
}
</style>
